import React from 'react';
import { FileText, User, Mail, Phone, MessageSquare } from 'lucide-react';
import { formatPhoneNumber } from '../../../utils/phoneFormatter';

export default function MeetingPurpose({ 
  purpose, 
  purposeDetails, 
  clientInfo, 
  onPurposeChange, 
  onPurposeDetailsChange, 
  onClientInfoChange 
}) {
  const purposes = [
    {
      value: 'initial_consultation',
      label: 'Initial Consultation',
      description: 'First-time consultation to discuss your bail needs and options',
      icon: '🤝'
    },
    {
      value: 'case_review',
      label: 'Case Review',
      description: 'Review existing case details and discuss next steps',
      icon: '📋'
    },
    {
      value: 'payment_discussion',
      label: 'Payment Discussion',
      description: 'Discuss payment plans, options, and financial arrangements',
      icon: '💳'
    },
    {
      value: 'document_signing',
      label: 'Document Signing',
      description: 'Sign bail bonds paperwork and complete necessary documentation',
      icon: '✍️'
    },
    {
      value: 'emergency_meeting',
      label: 'Emergency Meeting',
      description: 'Urgent meeting for time-sensitive bail situations',
      icon: '🚨'
    }
  ];

  const handleClientInfoChange = (field, value) => {
    // Auto-format phone number as user types
    if (field === 'phone') {
      const formatted = formatPhoneNumber(value);
      onClientInfoChange({
        ...clientInfo,
        [field]: formatted
      });
    } else {
      onClientInfoChange({
        ...clientInfo,
        [field]: value
      });
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">
        Meeting Purpose & Contact Info
      </h2>
      <p className="text-gray-600 mb-8">
        Tell us about your meeting needs and provide your contact information so we can reach you.
      </p>
      {/* Meeting Purpose Selection */}
      <div className="mb-8">
        <label className="flex items-center gap-2 text-sm font-semibold text-gray-900 mb-4">
          <FileText className="w-5 h-5 text-blue-600" />
          <span>Select Meeting Purpose *</span>
        </label>
        <div className="grid gap-4">
          {purposes?.map(p => (
            <button
              key={p?.value}
              onClick={() => onPurposeChange(p?.value)}
              className={`text-left p-4 rounded-lg border-2 transition-all hover:shadow-md ${
                purpose === p?.value
                  ? 'border-blue-600 bg-blue-50' :'border-gray-200 hover:border-blue-300'
              }`}
            >
              <div className="flex items-start gap-4">
                <span className="text-3xl">{p?.icon}</span>
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 mb-1">{p?.label}</h4>
                  <p className="text-sm text-gray-600">{p?.description}</p>
                </div>
                {purpose === p?.value && (
                  <div className="w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
      </div>
      {/* Additional Details */}
      <div className="mb-8">
        <label className="flex items-center gap-2 text-sm font-semibold text-gray-900 mb-3">
          <MessageSquare className="w-5 h-5 text-blue-600" />
          <span>Additional Details (Optional)</span>
        </label>
        <textarea
          value={purposeDetails}
          onChange={(e) => onPurposeDetailsChange(e?.target?.value)}
          placeholder="Provide any additional information about your meeting needs..."
          rows={4}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
        />
      </div>
      {/* Contact Information */}
      <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Contact Information</h3>
        
        <div className="grid gap-4">
          {/* Name */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <User className="w-4 h-4 text-gray-500" />
              <span>Full Name *</span>
            </label>
            <input
              type="text"
              value={clientInfo?.name}
              onChange={(e) => handleClientInfoChange('name', e?.target?.value)}
              placeholder="John Doe"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          {/* Email */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <Mail className="w-4 h-4 text-gray-500" />
              <span>Email Address *</span>
            </label>
            <input
              type="email"
              value={clientInfo?.email}
              onChange={(e) => handleClientInfoChange('email', e?.target?.value)}
              placeholder="john.doe@example.com"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          {/* Phone */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <Phone className="w-4 h-4 text-gray-500" />
              <span>Phone Number *</span>
            </label>
            <input
              type="tel"
              value={clientInfo?.phone}
              onChange={(e) => handleClientInfoChange('phone', e?.target?.value)}
              placeholder="(916) 555-0100"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>
        </div>

        <p className="mt-4 text-xs text-gray-500">
          * Required fields. Your information will be used only to contact you about this appointment.
        </p>
      </div>
    </div>
  );
}